import { api } from '@goaltree/backend/convex/_generated/api'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { CoachIntake } from '@/components/coach-intake'
import { clearIntakeAnonId, intakeAnonId } from '@/lib/intake-anon'
import { posthog } from '@/lib/posthog'

// "Set me up in 90 seconds": the coach's intake call, an alternative to the
// step-by-step onboarding. ?claim=true is the way back from signing up in the
// signed-out intake (/try): its answers become this account's, and ?live=true
// offers the live face they asked for.
export const Route = createFileRoute('/_authenticated/onboarding/intake')({
  validateSearch: (search: Record<string, unknown>): { claim?: boolean; live?: boolean } => ({
    claim: search.claim === true || search.claim === 'true' ? true : undefined,
    live: search.live === true || search.live === 'true' ? true : undefined,
  }),
  head: () => ({ meta: [{ title: 'Set me up — Goaltree' }] }),
  component: IntakeRoute,
})

function IntakeRoute() {
  const navigate = useNavigate()
  const { claim, live } = Route.useSearch()
  const claimAnon = useMutation(api.intake.claimAnon)
  const [claimed, setClaimed] = useState(!claim)
  const tried = useRef(false)

  useEffect(() => {
    if (!claim || tried.current) return
    tried.current = true
    const anonId = intakeAnonId()
    if (!anonId) return setClaimed(true)
    void claimAnon({ anonId })
      .then((r) => {
        clearIntakeAnonId()
        if (r) posthog.capture('intake_claimed', { next: r.next + 1, live: !!live })
      })
      .catch(() => {})
      .finally(() => setClaimed(true))
  }, [claim, live, claimAnon])

  if (!claimed)
    return (
      <div className="flex h-svh items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  return (
    <CoachIntake
      skipIntro={!!claim}
      resumeLive={!!live}
      onBack={() => void navigate({ to: '/goals/new' })}
    />
  )
}
